import { parseCsv } from "./csv";

/**
 * Cronometer CSV → Haus Meal rows.
 *
 * Cronometer's export format ("servings_DDDD.csv" or "Daily.csv") varies
 * slightly across years. We tolerate column headers in any order and pick
 * the relevant ones by name. Required columns:
 *   - Day        (date, YYYY-MM-DD)
 *   - Group      (e.g. "Breakfast", "Lunch")
 *   - Food Name
 *   - Energy (kcal)
 * Optional:
 *   - Time
 *   - Amount
 *   - Protein (g) / Carbs (g) / Fat (g)
 *
 * Returns parsed Meal entries grouped by (date, group). Multiple food rows
 * within the same meal aggregate kcal + macros and concatenate names.
 *
 * The export is per-serving (not per-meal), so we derive a Meal per
 * (date, group) pair. `name` becomes "Group: A; B; C" — Jonas can rename
 * later.
 *
 * Idempotency lives at the import step (caller skips if a Meal with the
 * same `source='imported_cronometer'` and same `ts` exists).
 */

export type CronometerRow = {
  date: string;
  group: string;
  foodName: string;
  amount: string | null;
  kcal: number;
  protein: number;
  carb: number;
  fat: number;
  time: string | null;
};

export type CronometerMeal = {
  date: string;
  group: string;
  ts: string;       // ISO local-naive datetime (YYYY-MM-DDTHH:MM)
  name: string;     // "Group: foodA; foodB"
  kcal: number;
  protein: number;
  carb: number;
  fat: number;
  rowCount: number;
};

const COL_DATE = ["day", "date"];
const COL_GROUP = ["group", "meal", "category"];
const COL_FOOD = ["food name", "food", "name"];
const COL_ENERGY = ["energy (kcal)", "energy", "kcal", "calories"];
const COL_PROTEIN = ["protein (g)", "protein"];
const COL_CARB = ["carbs (g)", "carbohydrates (g)", "carbs", "carbohydrates"];
const COL_FAT = ["fat (g)", "fat"];
const COL_AMOUNT = ["amount", "servings"];
const COL_TIME = ["time", "logged at"];

function indexOfHeader(headers: string[], candidates: string[]): number {
  const lc = headers.map((h) => h.trim().toLowerCase());
  for (const c of candidates) {
    const idx = lc.indexOf(c);
    if (idx !== -1) return idx;
  }
  return -1;
}

export function parseCronometerCsv(text: string): {
  rows: CronometerRow[];
  meals: CronometerMeal[];
  warnings: string[];
} {
  const warnings: string[] = [];
  const rows: CronometerRow[] = [];
  const grid = parseCsv(text);
  if (grid.length === 0) return { rows, meals: [], warnings: ["empty file"] };

  const headers = grid[0];
  const iDate = indexOfHeader(headers, COL_DATE);
  const iGroup = indexOfHeader(headers, COL_GROUP);
  const iFood = indexOfHeader(headers, COL_FOOD);
  const iEnergy = indexOfHeader(headers, COL_ENERGY);
  const iProtein = indexOfHeader(headers, COL_PROTEIN);
  const iCarb = indexOfHeader(headers, COL_CARB);
  const iFat = indexOfHeader(headers, COL_FAT);
  const iAmount = indexOfHeader(headers, COL_AMOUNT);
  const iTime = indexOfHeader(headers, COL_TIME);

  if (iDate === -1 || iGroup === -1 || iFood === -1 || iEnergy === -1) {
    warnings.push(
      "Required columns missing. Cronometer exports must include Day, Group, Food Name, Energy (kcal).",
    );
    return { rows, meals: [], warnings };
  }

  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r];
    if (!cells || cells.length === 0 || (cells.length === 1 && !cells[0].trim()))
      continue;

    const date = (cells[iDate] ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Cronometer sometimes outputs MM/DD/YYYY — normalize.
      const m = (cells[iDate] ?? "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) {
        const [, mm, dd, yyyy] = m;
        rows.push(toRow(`${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`));
      } else {
        warnings.push(`Row ${r + 1}: unrecognized date "${cells[iDate]}"`);
      }
      continue;
    }
    rows.push(toRow(date));

    function toRow(d: string): CronometerRow {
      return {
        date: d,
        group: (cells[iGroup] ?? "").trim() || "Other",
        foodName: (cells[iFood] ?? "").trim() || "Item",
        amount: iAmount >= 0 ? cells[iAmount] ?? null : null,
        kcal: numOrZero(cells[iEnergy]),
        protein: iProtein >= 0 ? numOrZero(cells[iProtein]) : 0,
        carb: iCarb >= 0 ? numOrZero(cells[iCarb]) : 0,
        fat: iFat >= 0 ? numOrZero(cells[iFat]) : 0,
        time: iTime >= 0 ? (cells[iTime] ?? "").trim() || null : null,
      };
    }
  }

  // Aggregate (date, group) into a Meal
  const buckets = new Map<string, CronometerMeal>();
  for (const r of rows) {
    const key = `${r.date}::${r.group}`;
    const ts = computeTs(r.date, r.group, r.time);
    const existing = buckets.get(key);
    if (existing) {
      existing.kcal += r.kcal;
      existing.protein += r.protein;
      existing.carb += r.carb;
      existing.fat += r.fat;
      existing.name += `; ${r.foodName}`;
      existing.rowCount += 1;
    } else {
      buckets.set(key, {
        date: r.date,
        group: r.group,
        ts,
        name: `${r.group}: ${r.foodName}`,
        kcal: r.kcal,
        protein: r.protein,
        carb: r.carb,
        fat: r.fat,
        rowCount: 1,
      });
    }
  }

  const meals = Array.from(buckets.values()).sort((a, b) =>
    a.ts.localeCompare(b.ts),
  );
  return { rows, meals, warnings };
}

function numOrZero(s: string | undefined): number {
  if (!s) return 0;
  const n = Number(s.trim().replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
}

const DEFAULT_TIMES: Record<string, string> = {
  Breakfast: "08:00",
  Lunch: "12:30",
  Dinner: "19:00",
  Snack: "15:00",
  Snacks: "15:00",
};

function computeTs(date: string, group: string, time: string | null): string {
  const t =
    (time && /^\d{1,2}:\d{2}/.test(time)
      ? time.slice(0, 5).padStart(5, "0")
      : DEFAULT_TIMES[group]) ?? "12:00";
  const [h, m] = t.split(":");
  return `${date}T${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
}
